import cn from "classnames";
import { useState, useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { DistributionLocation, SelectedRoute, SuggestedRoute } from "module/eddm/dto";
import { LocationLatLngType } from "components/place-autocomplete/type";
import Draggable from "react-draggable";
import { GoogleMap } from "@react-google-maps/api";
import {
  handleHoverSelectedMissionUpdate,
  handleHoverMultipleSelectedRoutes,
  getSelectedTargetsAsString,
} from "components/audience-map/utils";
import { VisiblePostCodeType } from "store/types";
import { useStore } from "store";
import { Check } from "lucide-react";
import { PlaceAutocomplete } from "components/place-autocomplete";
import { EmptyAddressesState } from "./components";
import { AddressCardV2 } from "./components/AddressCard/AddressCardV2";
import { AudienceAttributes } from "./components/AudienceAttributes/AudienceAttributes";
import { SuggestedRoutesBlock } from "./components/AddressCard/SuggestedRoutesBlock/SuggestedRoutesBlock";

interface AddressBlockProps {
  addresses: DistributionLocation[];
  onAddressCardClick: (address: DistributionLocation) => void;
  handleDeleteLocation: (deletedLocation: DistributionLocation) => void;
  onPlaceChanged: (location: LocationLatLngType) => void;
  getFilteredPostCodes?: (key: string) => VisiblePostCodeType[];
  selectedRoutes: SelectedRoute[];
  setSelectedRoutes: (selectedRoutes: SelectedRoute[] | ((selectedRoutes: SelectedRoute[]) => SelectedRoute[])) => void;
  googleMap: GoogleMap | null;
  setShouldAutoSave: (shouldAutoSave: boolean) => void;
  setIsUndoOrRedo: (isUndoOrRedo: boolean) => void;
  setIsSingleRouteSelection: (isSingleSelection: boolean) => void;
  setShouldHideStatistics: (shouldHideStatistics: boolean) => void;
  suggestedRoutes: SuggestedRoute[];
  setDrawingMode: (drawingMode: null | string) => void;
}

export const AddressBlockV3 = ({
  addresses,
  onAddressCardClick,
  handleDeleteLocation,
  onPlaceChanged,
  getFilteredPostCodes,
  selectedRoutes,
  setSelectedRoutes,
  googleMap,
  setShouldAutoSave,
  setIsUndoOrRedo,
  setIsSingleRouteSelection,
  setShouldHideStatistics,
  suggestedRoutes,
  setDrawingMode,
}: AddressBlockProps) => {
  const posthog = usePostHog();
  const postHogTracker = new EmitAnalyticsEvent(posthog);
  const {
    user,
    country,
    client: { name: clientName },
    map: { loading },
    campaign: { isSubmitted, id: campaignId },
    distributionLocations,
    selectedTargets,
    suggestionsEnabled,
    updateSelectedTargets,
    setSuggestionsEnabled,
  } = useStore();
  const noSuggestedRoutesSelectedBlockPosition = window.innerHeight / 3.5;
  const suggestedRoutesEnabledSelectedBlockInitialPosition = window.innerHeight * 0.73;
  const suggestedRoutesEnabledSelectedBlockTopBound = window.innerHeight / 2.5;
  const suggestedRoutesEnabledSelectedBlockBottomBound = window.innerHeight * 0.8;

  const [bounds, setBounds] = useState({
    top: suggestedRoutesEnabledSelectedBlockTopBound,
    bottom: suggestedRoutesEnabledSelectedBlockBottomBound,
    right: 0,
    left: 0,
  });

  const [controlledPosition, setControlledPosition] = useState({
    x: 0,
    y: suggestedRoutesEnabledSelectedBlockInitialPosition,
  });

  useEffect(() => {
    const newBounds = suggestionsEnabled
      ? {
          top: suggestedRoutesEnabledSelectedBlockTopBound,
          bottom: suggestedRoutesEnabledSelectedBlockBottomBound,
          right: 0,
          left: 0,
        }
      : {
          top: noSuggestedRoutesSelectedBlockPosition,
          bottom: noSuggestedRoutesSelectedBlockPosition,
          right: 0,
          left: 0,
        };
    setBounds(newBounds);
    if (!suggestionsEnabled) {
      setControlledPosition({ x: 0, y: noSuggestedRoutesSelectedBlockPosition });
    } else {
      setControlledPosition({ x: 0, y: suggestedRoutesEnabledSelectedBlockInitialPosition });
    }
  }, [suggestionsEnabled]);

  if (!addresses || addresses.length === 0) return null;

  const handleSuggestRoutes = () => {
    setDrawingMode(null);
    postHogTracker.run({
      eventName: "Clicked to get more route suggestions",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        audienceAttributes: getSelectedTargetsAsString(selectedTargets),
      },
    });
    setSuggestionsEnabled(true);
    setShouldAutoSave(true);
  };

  const onControlledDrag = (e: any, position: { x: number; y: number }) => {
    const { y } = position;
    setControlledPosition((prev) => ({ ...prev, y }));
  };

  return (
    <div
      className="cb-v2-root mob-hidden absolute left-0 top-0 flex h-full w-[360px] flex-col bg-card"
      onMouseEnter={() => setShouldHideStatistics(true)}
      onMouseLeave={() => setShouldHideStatistics(false)}
    >
      <div className="border-y border-solid border-border p-4">
        <div className="mb-[5px] flex items-center justify-start text-sm font-normal text-[#2E3860] dark:text-foreground">
          Add more locations
        </div>
        <div className="flex w-full">
          <PlaceAutocomplete
            countryCode={country?.code}
            disabled={isSubmitted || loading}
            placeholder={"Enter a city, neighborhood, or address"}
            postCodeSearch={true}
            onPlaceChanged={onPlaceChanged}
            getFilteredPostCodes={getFilteredPostCodes}
            dataCy="eddmMapSearchInput"
          />
        </div>
      </div>
      <AudienceAttributes
        suggestionsEnabled={suggestionsEnabled}
        selectedTargets={selectedTargets}
        updateSelectedTargets={updateSelectedTargets}
        onSubmit={handleSuggestRoutes}
        setShouldAutoSave={setShouldAutoSave}
        suggestedRoutes={suggestedRoutes}
      />

      {suggestionsEnabled && !!suggestedRoutes?.length && (
        <div>
          <SuggestedRoutesBlock
            suggestedRoutes={suggestedRoutes}
            googleMap={googleMap}
            setSelectedRoutes={setSelectedRoutes}
            setShouldAutoSave={setShouldAutoSave}
            setIsSingleRouteSelection={setIsSingleRouteSelection}
            setIsUndoOrRedo={setIsUndoOrRedo}
          />
        </div>
      )}
      <Draggable bounds={bounds} position={controlledPosition} onDrag={onControlledDrag}>
        <div
          className={cn(
            "absolute z-[1] flex w-full flex-col items-center rounded-t-2xl border border-b-0 border-solid border-[#B9C6E4] bg-white pt-2 dark:border-border dark:bg-card",
            suggestionsEnabled ? "h-[50vh]" : "h-[67vh]"
          )}
        >
          <div className="mb-2.5 h-1 w-[46px] cursor-pointer rounded bg-border" />
          <div
            className={cn(
              "w-full px-4 py-5",
              addresses.length === 1 && !selectedRoutes?.length ? "overflow-hidden" : "overflow-scroll"
            )}
          >
            <span
              className="group relative flex w-fit cursor-default items-center overflow-hidden py-1 pl-0.5 pr-[5px] font-bold text-foreground transition-colors duration-[400ms] ease-out before:absolute before:inset-0 before:z-0 before:-translate-x-[101%] before:bg-[#4F5B94] before:transition-transform before:duration-[400ms] before:ease-out before:content-[''] hover:text-white hover:before:translate-x-0 [&_span]:relative [&_span]:z-[1] [&_span]:flex [&_span]:items-center [&_svg]:relative [&_svg]:z-[1]"
              onMouseOver={() => {
                handleHoverMultipleSelectedRoutes({ mapInit: googleMap, selectedRoutes });
              }}
              onMouseLeave={() => handleHoverSelectedMissionUpdate({ mapInit: googleMap, hoveredMission: null })}
            >
              <Check className="mr-2 h-4 w-4" /> <span>Selected routes</span>
            </span>
            {addresses.map((address) => {
              return (
                <AddressCardV2
                  address={address}
                  key={address.name}
                  onClick={() => onAddressCardClick(address)}
                  handleDeleteLocation={handleDeleteLocation}
                  setSelectedRoutes={setSelectedRoutes}
                  googleMap={googleMap}
                  setShouldAutoSave={setShouldAutoSave}
                  setIsUndoOrRedo={setIsUndoOrRedo}
                  setIsSingleRouteSelection={setIsSingleRouteSelection}
                />
              );
            })}
          </div>
          {!selectedRoutes.length && distributionLocations?.length === 1 && (
            <div className="flex h-1/2 flex-col items-center justify-start">
              <EmptyAddressesState />
              <span className="mt-2.5 w-[184px] text-center text-foreground">
                Click on the map areas to choose your delivery routes.
              </span>
            </div>
          )}
        </div>
      </Draggable>
    </div>
  );
};
